import React, { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import axios from 'axios';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNotch,
  faArrowLeft,
  faArrowRight,
} from '@fortawesome/pro-light-svg-icons';
import PriceModal from './PriceModal';
import Month from './Month';
import { loadErrorMessage } from '../../services/requests';
import {SET_SELECTED_DATE} from '../../redux/quote';
import { requestDatePrice } from '../../redux/actions/datePrice';
import DatePriceStatus from '../DatePriceStatus';


const moment = extendMoment(Moment);

function SingleCalendar({ dispatch, config, quote }) {
  const [loading, setLoading] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(quote.selectedDate.date || (_.isEmpty(quote.urlDate) ? moment() : quote.urlDate));
  const [openModal, setOpenModal] = useState(false);
  const [startDate, setStartDate] = useState(moment());

  const mounted = useRef(true);
  const attemptedDate = useRef();
  const [error, setError] = useState(null);
  useEffect(() => () => { mounted.current = false; }, []);

  const defaultSelectedDateIfBlocked = (blockedDates, dateToCheck) => {
    let effectiveDate = dateToCheck || selectedDate;
    if (blockedDates[effectiveDate.format('DD-MM-YYYY')]) {
      let newDate = moment();
      // It's possible today's date might also be blocked, so loop until we find a non-blocked day
      while(blockedDates[newDate.format('DD-MM-YYYY')]) {
        newDate = newDate.add(1, 'days');
      }
      dispatch({
        type: SET_SELECTED_DATE,
        payload: { date: newDate },
      });
      return newDate;
    }
    return effectiveDate;
  };

  const fetchPriceForDate = (date) => {
    dispatch(requestDatePrice(date));
  };

  const getAvailabilityData = () => {
    setLoading(true);
    setError(null);
    axios
      .get(`${config.appUrl}/api/v2/availability/${config.location.id}`)
      .then((response) => {
        if (!mounted.current) return;
        setBlockedDates(response.data);
        const defaultDate = attemptedDate.current || quote.selectedDate.date || (config.startDate
          ? moment(config.startDate, 'DD-MM-YYYY')
          : (_.isEmpty(quote.urlDate) ? moment() : quote.urlDate.clone()));
        const effectiveDate = defaultSelectedDateIfBlocked(response.data, defaultDate);
        if (effectiveDate) {
          setSelectedDate(effectiveDate);
        }
        if (quote.eventType && quote.eventType.id && !config.hidePricing) {
          fetchPriceForDate(effectiveDate);
        }
        setLoading(false);
      })
      .catch((error) => {
        // A network failure must not surface as an unhandled rejection or leave the
        // calendar stuck loading (MYEVENT-QUOTE-2).
        if (!mounted.current) return;
        setLoading(false);
        setError({ message: loadErrorMessage(error, 'availability') });
      });
  };

  useEffect(() => {
    getAvailabilityData();
    if (config.startDate) {
      const configStart = moment(config.startDate, "DD-MM-YYYY");
      if (!quote.selectedDate.date) setSelectedDate(configStart);
      setStartDate(configStart);
    }
  },[config.startDate]);

  const changeDate = (date) => {
    attemptedDate.current = date;
    setSelectedDate(date);
    fetchPriceForDate(date);
  };

  const previous = () => {
    if (selectedDate.isAfter(startDate)) {
      setSelectedDate(selectedDate.clone().subtract(1, 'month'));
    }
  };

  const next = () => {
    setSelectedDate(selectedDate.clone().add(1, 'month'));
  };

  return (
    <div id="cordero-availability-calendar">
      <DatePriceStatus />
      {error && <div className="alert alert-danger" role="alert">
        <p>{error.message}</p>
        <button type="button" className="btn btn-primary" onClick={() => error.date ? fetchPriceForDate(error.date) : getAvailabilityData()}>Retry</button>
      </div>}
      <div className="row navigation">
        <div className="col-2">
          <button type="button" aria-label="Previous month" onClick={() => previous()} disabled={selectedDate.isBefore(config.startDate)}>
            <FontAwesomeIcon icon={faArrowLeft} size="2x" />
          </button>
        </div>
        <div className="col-8 text-center">
          <h5>{(!_.isEmpty(selectedDate)) ? moment(selectedDate).format('MMMM YYYY') : moment().format('MMMM YYYY')}</h5>
        </div>
        <div className="col-2 text-end">
          <button type="button" aria-label="Next month" onClick={() => next()}
            disabled={
                config.maxDisplayYear &&
                selectedDate.month() == 11 &&
                selectedDate.year() == config.maxDisplayYear
                }>
            <FontAwesomeIcon icon={faArrowRight} size="2x" />
          </button>
        </div>
      </div>
      <div>
        <div>
          <div className="calendar">
            {!loading && (!error || error.date) && <Month
              month={selectedDate}
              selectedDate={selectedDate}
              blockedDates={blockedDates}
              changeDate={(e) => changeDate(e)}
              hideHeader
            />}
            {loading && (
              <div className="loading-container">
                <div>
                  <FontAwesomeIcon icon={faCircleNotch} size="4x" spin />
                </div>
                <h4>Loading Availability</h4>
              </div>
            )}
          </div>
          { (!_.isUndefined(quote.selectedDate.price) && quote.selectedDate.price != 0 && !config.hidePricing) &&
            <div className="text-center">
              <h5>The price for your chosen date is</h5>
              <h4>£{ quote.selectedDate.price.toFixed(2)}</h4>
            </div>
          }
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({ config: state.config, quote: state.quote });

export default connect(mapStateToProps)(SingleCalendar);
